// What the panel drops when there is no room for it.
//
// A phone-width player gives the panel about 300 pixels. A full SHA-256
// fingerprint is 64 characters of monospace: at that width it wraps to five
// lines and pushes everything below it off the bottom, so the one screen that
// exists to make verification legible becomes the least legible thing on the
// page.
//
// Pure and tested for the usual reason (see panelModel.ts): vitest here is
// node-only, so anything a .tsx decides cannot be checked. These are the
// decisions; the components are layout.

/**
 * Below this the panel is treated as narrow. Chosen from the content, not from
 * a device: a shortened fingerprint plus its label needs roughly 300px, and
 * below about 520px the two-column label/value grid stops fitting one on a
 * line. The player has its own `NARROW_CONTROLS_WIDTH` (560) for the control
 * row; these are different questions about different boxes and deliberately
 * not shared.
 */
export const NARROW_PANEL_WIDTH = 520

export function isNarrowPanel(widthPx: number): boolean {
  return widthPx > 0 && widthPx < NARROW_PANEL_WIDTH
}

/**
 * Below this player height the panel's type shrinks, however wide it is. A
 * phone on its side is wide enough for the two-column layout but gives the
 * panel only ~340px of height, and at full size the heading alone wrapped to
 * two lines of it (Gavin, 16 Oct, iPhone). Measured on the player, not the
 * panel: the panel's own height follows its content, so it cannot say how
 * much room there is.
 */
export const SHORT_PLAYER_HEIGHT = 500

export function isShortPlayer(heightPx: number): boolean {
  return heightPx > 0 && heightPx < SHORT_PLAYER_HEIGHT
}

/** How many characters survive at each end of a shortened fingerprint. */
const KEEP = 8

/**
 * A fingerprint short enough to read on a phone.
 *
 * Both ends, never just the front: a viewer comparing this against a block
 * explorer needs two anchors, and a leading fragment alone can be matched by
 * accident far more easily than a leading AND trailing one. The ellipsis is a
 * real character rather than three dots so a copy-paste is obviously partial.
 *
 * A hash short enough to show whole is returned unchanged — shortening
 * something that already fits only makes it less useful.
 */
export function shortFingerprint(hash: string): string {
  if (hash.length <= KEEP * 2 + 1) return hash
  return `${hash.slice(0, KEEP)}…${hash.slice(-KEEP)}`
}

/** The fingerprint as the panel should show it at this width. */
export function fingerprintForWidth(hash: string, narrow: boolean): string {
  return narrow ? shortFingerprint(hash) : hash
}
